"use client";

import { useState, useRef, type UIEvent, useEffect } from "react";
import CardUI from "~/components/ui/card";
import GreeterUI from "~/components/ui/greeter";
import { useTokenAnalysis } from "~/lib/tokenizer";
import { pageConfig } from "~/config/_pages/tools/tokenizer";
import { useSearchParams } from "next/navigation";
import { cn } from "~/lib/utils";

const HIGHLIGHT_COLORS = [
  "bg-red-400/100",
  "bg-sky-400/60",
  "bg-green-400/60",
  "bg-yellow-400/60",
  "bg-purple-400/60",
  "bg-pink-400/60",
  "bg-teal-400/60",
];

const MAX_TOKENS = pageConfig.max_token;

export default function TokenizerPage() {
  const inputText = useSearchParams().get("input");
  const [text, setText] = useState(inputText ?? "");
  const { tokens, segments, cost } = useTokenAnalysis(text);
  const backdropRef = useRef<HTMLDivElement>(null);

  const handleScroll = (e: UIEvent<HTMLTextAreaElement>) => {
    if (backdropRef.current) {
      backdropRef.current.scrollTop = e.currentTarget.scrollTop;
      backdropRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const percentage = ((tokens.length / MAX_TOKENS) * 100).toFixed(2);

  const getDirection = (text: string) => {
    if (!text) return "rtl";
    const persianRegex = /^[\u0600-\u06FF\s]+/;
    return persianRegex.test(text.trim()) ? "rtl" : "ltr";
  };
  const [direction, setDirection] = useState<"rtl" | "ltr">("rtl");
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    setDirection(getDirection(newText));
  };

  useEffect(()=> {
    if (inputText) setDirection(getDirection(inputText));
  }, [inputText])

  return (
    <div className="container mx-auto max-w-7xl pb-32" dir="rtl">
      <GreeterUI className="mb-8">
        <GreeterUI.Sub text="محاسبه دقیق هزینه هر متن" />
        <GreeterUI.Title text="ابزار توکنایز" />
      </GreeterUI>

      <CardUI
        variant="morph"
        className="relative h-80 w-full overflow-hidden rounded-xl text-left shadow-sm"
        dir={direction}
      >
        <div
          ref={backdropRef}
          className={cn("pointer-events-none absolute inset-0 overflow-auto p-4 font-mono text-base leading-relaxed wrap-break-word whitespace-pre-wrap text-transparent", direction==="rtl" && "text-right")}
        >
          {segments.map((segment, index) => (
            <span
              key={`${segment.start}-${index}`}
              className={`${HIGHLIGHT_COLORS[index % HIGHLIGHT_COLORS.length]} rounded-sm`}
            >
              {segment.text}
            </span>
          ))}
        </div>

        <textarea
          value={text}
          onChange={handleTextChange}
          onScroll={handleScroll}
          placeholder="متن خود را اینجا تایپ کنید..."
          className="absolute inset-0 h-full w-full resize-none bg-transparent p-4 font-mono text-base leading-relaxed text-white caret-white outline-none"
          spellCheck={false}
        />
      </CardUI>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <CardUI
          variant="morph"
          className="flex flex-col items-center justify-center rounded-xl p-6"
        >
          <span className="mb-2 text-sm text-gray-300">تعداد کل توکن‌ها</span>
          <span className="text-3xl font-bold text-blue-600">
            {tokens.length.toLocaleString()}
          </span>
        </CardUI>

        <CardUI
          variant="morph"
          className="flex flex-col items-center justify-center rounded-xl p-6"
        >
          <span className="mb-2 text-sm text-gray-300">
            درصد از سقف ({MAX_TOKENS.toLocaleString()})
          </span>
          <span className="text-3xl font-bold text-orange-500">
            {percentage}%
          </span>
          <div className="mt-3 h-2 w-full rounded-full bg-gray-200">
            <div
              className="h-2 rounded-full bg-orange-500 transition-all duration-300"
              style={{ width: `${Math.min(Number(percentage), 100)}%` }}
            ></div>
          </div>
        </CardUI>

        <CardUI
          variant="morph"
          className="flex flex-col items-center justify-center rounded-xl p-6"
        >
          <span className="mb-2 text-sm text-gray-300">تخمین هزینه (دلار)</span>
          <span className="text-3xl font-bold text-green-600" dir="ltr">
            ${cost.toFixed(6)}
          </span>
          <span className="mt-2 text-xs text-gray-400">
            بر اساس {pageConfig.model_title}
          </span>
        </CardUI>
      </div>
    </div>
  );
}
